import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';

/**
 * Reusable pagination bar with:
 *  - Prev / Next buttons
 *  - Numbered page buttons (with ellipsis for large ranges)
 *  - "Go to page" input for direct jumps on 30+ page datasets
 *
 * Preserves upstream filters/sorting automatically — the parent only owns
 * the current page state, so filter/sort props stay untouched on every jump.
 *
 * Props:
 *  - currentPage    (1-indexed number)
 *  - totalPages     (number)
 *  - onPageChange   ((page: number) => void)
 *  - totalItems     (optional, for the "Showing X - Y of N" label)
 *  - pageSize       (optional, for the "Showing X - Y of N" label)
 *  - itemLabel      (optional plural, e.g. "families"; defaults to "items")
 *  - testIdPrefix   (optional string, to disambiguate multiple bars on one screen)
 */
const PaginationBar = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  itemLabel = 'items',
  testIdPrefix = '',
}) => {
  const [jumpValue, setJumpValue] = useState(String(currentPage));

  useEffect(() => {
    setJumpValue(String(currentPage));
  }, [currentPage]);

  if (!totalPages || totalPages < 1) return null;

  const goToPage = (page) => {
    const p = Number(page);
    if (Number.isFinite(p) && p >= 1 && p <= totalPages && p !== currentPage) {
      onPageChange(p);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) if (!pages.includes(i)) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...');
    if (!pages.includes(totalPages)) pages.push(totalPages);
    return pages;
  };

  const commitJump = () => {
    const p = parseInt(jumpValue, 10);
    if (Number.isFinite(p) && p >= 1 && p <= totalPages) {
      goToPage(p);
    } else {
      setJumpValue(String(currentPage));
    }
  };

  const showRange = totalItems && pageSize;
  const rangeStart = showRange ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = showRange ? Math.min(currentPage * pageSize, totalItems) : 0;

  const tid = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix);

  return (
    <div className="mt-6 flex flex-col items-center gap-3">
      {showRange && (
        <p className="text-sm text-gray-500" data-testid={tid('pagination-info')}>
          Showing {rangeStart} - {rangeEnd} of {totalItems} {itemLabel}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap justify-center">
        <Button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          variant="outline"
          size="sm"
          className="px-3 h-10 min-h-10"
          data-testid={tid('prev-page-btn')}
        >
          ← Prev
        </Button>

        {getPageNumbers().map((page, index) =>
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="px-2 text-gray-400 select-none">
              …
            </span>
          ) : (
            <Button
              key={page}
              onClick={() => goToPage(page)}
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              className={`w-10 h-10 min-h-10 ${currentPage === page ? 'bg-primary text-white' : ''}`}
              data-testid={tid(`page-${page}-btn`)}
            >
              {page}
            </Button>
          )
        )}

        <Button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          variant="outline"
          size="sm"
          className="px-3 h-10 min-h-10"
          data-testid={tid('next-page-btn')}
        >
          Next →
        </Button>
      </div>

      {totalPages > 5 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Go to page:</span>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            onBlur={commitJump}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitJump();
              }
            }}
            className="w-20 px-2 py-2 h-10 border border-gray-300 rounded text-center focus:ring-2 focus:ring-primary focus:border-transparent"
            data-testid={tid('page-jump-input')}
          />
          <span className="text-gray-400">of {totalPages}</span>
        </div>
      )}
    </div>
  );
};

export default PaginationBar;
